import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import keycloak, { initKeycloak } from './keycloak';

const AuthContext = createContext(null);

function userLabelFromToken() {
  const p = keycloak.tokenParsed;
  if (!p) return null;
  return p.preferred_username || p.email || p.name || null;
}

export function AuthProvider({ children }) {
  const [ready, setReady] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [userLabel, setUserLabel] = useState(null);

  const syncAuthState = useCallback(() => {
    const auth = Boolean(keycloak.authenticated);
    setAuthenticated(auth);
    setUserLabel(auth ? userLabelFromToken() : null);
  }, []);

  useEffect(() => {
    let cancelled = false;

    initKeycloak()
      .then(() => {
        if (cancelled) return;
        syncAuthState();
        setReady(true);
      })
      .catch(() => {
        if (!cancelled) setReady(true);
      });

    keycloak.onAuthSuccess = () => syncAuthState();
    keycloak.onAuthLogout = () => syncAuthState();
    keycloak.onAuthRefreshSuccess = () => syncAuthState();
    keycloak.onTokenExpired = () => {
      keycloak.updateToken(30).catch(() => keycloak.logout());
    };

    return () => {
      cancelled = true;
    };
  }, [syncAuthState]);

  const login = useCallback(() => {
    return keycloak.login({ redirectUri: window.location.href });
  }, []);

  const register = useCallback(() => {
    return keycloak.register({ redirectUri: window.location.href });
  }, []);

  const logout = useCallback(() => {
    const redirectUri = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    return keycloak.logout({ redirectUri });
  }, []);

  const value = useMemo(
    () => ({
      ready,
      authenticated,
      userLabel,
      login,
      register,
      logout,
    }),
    [ready, authenticated, userLabel, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
